import moment from 'moment'
import _ from 'lodash'

import InvoicesRepository from '../infra/InvoicesRepository'
import Utils from './Utils'

export default class NewInvoiceEditor {

  initialize = props => {
    if (props.teamId) {
      this.teamId = props.teamId
      this.title = 'INVOICE'
      this.notes = ''
      this.number = 0
      this.total = 0
      this.paid = 0
      this.paymentDueDate = moment()
      this.invoiceDate = moment()
      this.items = []
      this.invoicePhotos = []
      this.customerInfo = null
      this.customerId = null
      this.contactId = null
      this.referencedEvents = []
      // Query the last invoice created to get the next
      // invoice number as a one increment.
      InvoicesRepository.getLastInvoice(this.teamId, this.onLastInvoiceValue)
    }
  }

  getTeamId = () => this.teamId
  
  getBillingEntity = () => ({
    title: this.title,
    invoiceDate: this.invoiceDate.toISOString(),
    number: this.number,
    notes: this.notes,
    total: this.total,
    paid: 0,
    customerName: this.customerInfo && this.customerInfo.customer && this.customerInfo.customer.name || '',
    customerId: this.getCustomerId() || '',
    contactId: this.getContactId() || '',
    paymentDueDate: this.paymentDueDate.toISOString(),
    status: 'draft',
    creationDate: moment().toISOString()
  })

  release = () => {}

  notifyValueUpdatedListener = () => {
    if (this.valueUpdatedListener) {
      this.valueUpdatedListener()
    }
  }

  addValueUpdatedListener = listener => {
    this.valueUpdatedListener = listener
  }

  onLastInvoiceValue = snapshot => {
    const result = snapshot && snapshot.val()
    const list = _.map(result)
    const number = list && list.length && list[0] && list[0].number || 0
    this.setNumber(number + 1)
  }

  getCustomerId = () => this.customerInfo && this.customerInfo.customerId
  
  getContactId = () => this.customerInfo && this.customerInfo.contactId

  setCustomerInfo = customerInfo => {
    this.customerInfo = customerInfo
    this.notifyValueUpdatedListener()
  }

  getContactInfo = () => this.customerInfo && this.customerInfo.contact
  
  getCustomerInfo = () => this.customerInfo

  isLoadingItems = () => false

  getItems = () => this.items

  addItem = item => {
    this.items.push(Object.assign({}, item))
    this.total += Utils.getItemAmount(item)
    this.notifyValueUpdatedListener()
  }

  deleteItem = item => {
    this.items = this.items.filter(i => i !== item)
    this.total -= Utils.getItemAmount(item)
    this.notifyValueUpdatedListener()
  }

  updateItemName = (item, name) => {
    const updated = this.items.find(i => i === item)
    if (updated) {
      updated.name = name
      this.notifyValueUpdatedListener() 
    }
  }

  updateItemDescription = (item, description) => {
    const updated = this.items.find(i => i === item)
    if (updated) {
      updated.description = description
      this.notifyValueUpdatedListener() 
    }
  }

  updateItemRate = (item, rate) => {
    const updated = this.items.find(i => i === item)
    const oldAmount = Utils.getItemAmount(updated)
    if (updated) {
      updated.rate = rate
      const newAmount = Utils.getItemAmount(updated)
      this.total += newAmount - oldAmount
      this.notifyValueUpdatedListener() 
    }
  }

  updateItemTaxPercent = (item, taxPercent) => {
    const updated = this.items.find(i => i === item)
    const oldAmount = Utils.getItemAmount(updated)
    if (updated) {
      updated.taxPercent = taxPercent
      const newAmount = Utils.getItemAmount(updated)
      this.total += newAmount - oldAmount
      this.notifyValueUpdatedListener() 
    }
  }

  updateItemQuantity = (item, quantity) => {
    const updated = this.items.find(i => i === item)
    const oldAmount = Utils.getItemAmount(updated)
    if (updated) {
      updated.quantity = quantity
      const newAmount = Utils.getItemAmount(updated)
      this.total += newAmount - oldAmount
      this.notifyValueUpdatedListener() 
    }
  }

  getTotal = () => this.total

  getTitle = () => {
    return this.title
  }

  setTitle = title => {
    this.title = title
    this.notifyValueUpdatedListener()
  }

  getNumber = () => {
    return this.number
  }

  setNumber = number => {
    this.number = number
    this.notifyValueUpdatedListener()
  }

  getNotes = () => {
    return this.notes
  }

  setNotes = notes => {
    this.notes = notes
    this.notifyValueUpdatedListener()
  }

  getPaymentDueDate = () => {
    return this.paymentDueDate
  }

  setPaymentDueDate = date => {
    this.paymentDueDate = date
    this.notifyValueUpdatedListener()
  }

  getBillDate = () => {
    return this.invoiceDate
  }

  setBillDate = date => {
    this.invoiceDate = date
    this.notifyValueUpdatedListener()
  }

  isLoadingReferencedEvents = () => false

  getReferencedEvents = () => this.referencedEvents

  addReferencedEvent = eventInfo => {
    this.referencedEvents.push(eventInfo)
    this.notifyValueUpdatedListener()
  }

  removeReferencedEvent = eventInfo => {
    this.referencedEvents = this.referencedEvents
      .filter(e => !(e.endDate === eventInfo.endDate && e.eventId === eventInfo.eventId))
    this.notifyValueUpdatedListener()
  }

  isLoadingPhotos = () => false
  
  getPhotos = () => this.invoicePhotos

  addPhotos = photos => {
    for (const photo of photos) {
      const found = this.invoicePhotos.find(p => p.photoId === photo.photoId)
      if (!found) {
        this.invoicePhotos.push(photo)
      }
    }
    this.notifyValueUpdatedListener()
  }

  validateInvoice = () => {
    // Validate invoice properties
    if (!this.invoiceDate || !this.invoiceDate.isValid()) {
      return { error: 'Invoice date is not set or has an invalid value.' }
    }
    if (!this.paymentDueDate || !this.paymentDueDate.isValid()) {
      return { error: 'Payment due date is not set or has an invalid value.' }
    }
    // Validate invoice items
    if (!this.items.length) {
      return { error: 'Invoice items are required. Please input at least one invoice item before saving the invoice.' }
    }
    for (let index = 0; index < this.items.length; index++) {
      const item = this.items[index]
      if (!item.name) {
        return { error: `Invoice items name are required. Please check the invoice item at index ${1 + index}` }
      }
      if (!item.rate || isNaN(item.rate)) {
        return { error: `Invoice items rate are required. Please check the invoice item at index ${1 + index}` }
      }
      if (!item.quantity || isNaN(item.quantity)) {
        return { error: `Invoice items rate are required. Please check the invoice item at index ${1 + index}` }
      }
    }
    return { success: true }
  }

  save = () => {
    const validationResult = this.validateInvoice()
    if (validationResult.success) {
      const invoice = this.getBillingEntity()
      InvoicesRepository.createNewInvoice(this.teamId,
        invoice, this.items, this.invoicePhotos, this.referencedEvents)
      return Object.assign({ invoice }, validationResult)
    }
    return validationResult
  }

  /**
   * Invoice creation disables deletion
   */
  delete = () => {}

}



// WEBPACK FOOTER //
// ./src/billing/infra/NewInvoiceEditor.js